/**
 * grundpreis — Grundpreis (PAngV) und Gewicht, kompatibel zu Shopify.
 *
 * Einheiten werden als Shopify-Enum-Werte gespeichert (G, KG, ML, CL, L,
 * M, M2, ITEM), damit der Roundtrip FieldConnect ↔ Shopify verlustfrei ist.
 * Reine Funktionen, kein React, kein I/O.
 */

/** Einheiten mit Basis-Dimension und Faktor zur Basiseinheit */
export const GRUNDPREIS_EINHEITEN = [
  { value: 'G',    label: 'g',     basis: 'KG',   faktor: 0.001 },
  { value: 'KG',   label: 'kg',    basis: 'KG',   faktor: 1 },
  { value: 'ML',   label: 'ml',    basis: 'L',    faktor: 0.001 },
  { value: 'CL',   label: 'cl',    basis: 'L',    faktor: 0.01 },
  { value: 'L',    label: 'l',     basis: 'L',    faktor: 1 },
  { value: 'M',    label: 'm',     basis: 'M',    faktor: 1 },
  { value: 'M2',   label: 'm²',    basis: 'M2',   faktor: 1 },
  { value: 'ITEM', label: 'Stück', basis: 'ITEM', faktor: 1 },
];

/** Übliche Referenzmengen (PAngV § 5: 1 kg, 1 l, 100 g, 100 ml, 1 m, 1 m², 1 Stück) */
export const REFERENZ_OPTIONEN = [
  { menge: 1,   einheit: 'KG' },
  { menge: 100, einheit: 'G' },
  { menge: 1,   einheit: 'L' },
  { menge: 100, einheit: 'ML' },
  { menge: 1,   einheit: 'M' },
  { menge: 1,   einheit: 'M2' },
  { menge: 1,   einheit: 'ITEM' },
];

const byValue = (v) => GRUNDPREIS_EINHEITEN.find((e) => e.value === v) || null;

/** Label einer Einheit (z. B. 'KG' → 'kg') */
export function einheitLabel(value) {
  return byValue(value)?.label || value || '';
}

/** Standard-Referenz zur gewählten Inhaltseinheit */
export function defaultReferenz(einheit) {
  const e = byValue(einheit);
  if (!e) return null;
  return { menge: 1, einheit: e.basis };
}

/**
 * Grundpreis berechnen.
 * @returns {number|null} Preis je Referenzmenge, null wenn nicht berechenbar
 */
export function computeGrundpreis({ preis, menge, einheit, refMenge, refEinheit }) {
  const p = Number(preis);
  const m = Number(menge);
  const rm = Number(refMenge);
  const e = byValue(einheit);
  const re = byValue(refEinheit);
  if (!Number.isFinite(p) || !Number.isFinite(m) || !Number.isFinite(rm)) return null;
  if (p < 0 || m <= 0 || rm <= 0 || !e || !re) return null;
  if (e.basis !== re.basis) return null;
  const inhaltBasis = m * e.faktor;
  const refBasis = rm * re.faktor;
  return (p / inhaltBasis) * refBasis;
}

const fmtDE = (n, min = 2, max = 2) =>
  Number(n).toLocaleString('de-DE', { minimumFractionDigits: min, maximumFractionDigits: max });

/** "12,50 €/kg" bzw. "0,83 €/100 g" */
export function formatGrundpreis(wert, refMenge, refEinheit) {
  if (wert == null || !Number.isFinite(Number(wert))) return '—';
  const rm = Number(refMenge) || 1;
  const label = einheitLabel(refEinheit);
  const ref = rm === 1 ? label : `${fmtDE(rm, 0, 0)} ${label}`;
  return `${fmtDE(wert)} €/${ref}`;
}

/**
 * Varianten-Zeile → Shopify-Input (unitPriceMeasurement + showUnitPrice).
 * @returns {object|null} null wenn keine vollständige Angabe vorliegt
 */
export function toShopifyUnitPrice(row) {
  const menge = Number(row?.grundpreis_menge);
  const refMenge = Number(row?.grundpreis_referenz_menge);
  const einheit = row?.grundpreis_einheit;
  const refEinheit = row?.grundpreis_referenz_einheit;
  if (!Number.isFinite(menge) || menge <= 0 || !byValue(einheit)) return null;
  if (!Number.isFinite(refMenge) || refMenge <= 0 || !byValue(refEinheit)) return null;
  return {
    unitPriceMeasurement: {
      quantityValue: menge,
      quantityUnit: einheit,
      referenceValue: Math.round(refMenge),
      referenceUnit: refEinheit,
    },
    showUnitPrice: !!row?.grundpreis_anzeigen,
  };
}

/**
 * Shopify unitPriceMeasurement → Spalten der Variante.
 * @returns {object|null} null wenn Shopify keine (gültige) Messung liefert
 */
export function fromShopifyUnitPrice(upm) {
  if (!upm) return null;
  const q = Number(upm.quantityValue);
  const r = Number(upm.referenceValue);
  if (!Number.isFinite(q) || q <= 0 || !byValue(upm.quantityUnit)) return null;
  if (!Number.isFinite(r) || r <= 0 || !byValue(upm.referenceUnit)) return null;
  return {
    grundpreis_menge: q,
    grundpreis_einheit: upm.quantityUnit,
    grundpreis_referenz_menge: Math.round(r),
    grundpreis_referenz_einheit: upm.referenceUnit,
  };
}

const KG_PER_UNIT = {
  KILOGRAMS: 1,
  GRAMS: 0.001,
  POUNDS: 0.45359237,
  OUNCES: 0.028349523125,
};

/** Shopify-Gewicht → Kilogramm (null bei fehlendem/ungültigem Wert) */
export function weightToKg(value, unit) {
  if (value == null || value === '') return null;
  const v = Number(value);
  const f = KG_PER_UNIT[unit];
  if (!Number.isFinite(v) || v < 0 || !f) return null;
  return Math.round(v * f * 10000) / 10000;
}

/** Kilogramm → Shopify-Gewichtsinput */
export function kgToShopifyWeight(kg) {
  if (kg == null || kg === '') return null;
  const v = Number(kg);
  if (!Number.isFinite(v) || v < 0) return null;
  return { value: v, unit: 'KILOGRAMS' };
}

/** "0,4500 kg" / "450 g" für Listen */
export function formatGewicht(kg) {
  const v = Number(kg);
  if (kg == null || !Number.isFinite(v)) return '—';
  if (v < 1) return `${fmtDE(v * 1000, 0, 0)} g`;
  return `${fmtDE(v, 0, 3)} kg`;
}
